import { useState, useMemo, useEffect } from 'react';
import { AgentOverlapCard } from '@/components/admin/AgentOverlapCard';
import { Link, useLocation } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ordersApi, ameexApi, coversApi, coverSearchesApi, resumeLinkApi, type Order, type Cover } from '@/lib/api';
import { renderMessage } from '@/lib/followUpMessages';
import { supabase } from '@/lib/supabase';
import { AdminLayout } from '@/components/AdminLayout';
import { AmeexInsightsCard } from '@/components/admin/AmeexInsightsCard';
import { ConfirmedOrdersCard } from '@/components/admin/ConfirmedOrdersCard';
import { ProfitabilityCard } from '@/components/admin/ProfitabilityCard';
import { RecentOrdersCard } from '@/components/admin/RecentOrdersCard';
import { ProfitCalculator } from '@/components/admin/home/ProfitCalculator';
import { AiCostsCard } from '@/components/admin/AiCostsCard';
import { AiCallsList } from '@/components/admin/AiCallsList';
import { DateRangePicker, presetRange, type DateRange } from '@/components/admin/DateRangePicker';
import { formatPhone } from '@/lib/utils';
import { sourceMeta } from '@/lib/trafficSource';
import { useT } from '@/lib/i18n';
import { TONE, chip, type Tone, type ToneName } from '@/components/admin/dashTone';
import { dashboardApi, type StaleResponse } from '@/lib/dashboardApi';
import { rateOrTooEarly, formatDH, MIN_SAMPLE } from '@/lib/homeDashboard';
import { usePermissions } from '@/lib/permissionsStore';
import { useAuthStore } from '@/store/useAuthStore';
import { CouponsReportLine } from './coupons/CouponsReportLine';

// ─── Time range helpers ───────────────────────────────────────────────────────
// Number of whole days spanned by a {from,to} range; 0 = "all" (no from bound).
function spanDays(dates: DateRange): number {
  if (!dates.from) return 0;
  const from = new Date(dates.from).getTime();
  const to = new Date(dates.to ?? new Date().toISOString()).getTime();
  return Math.max(1, Math.round((to - from) / 86_400_000) + 1);
}

// ─── KPI Card ─────────────────────────────────────────────────────────────────

function KpiCard({
  icon,
  label,
  value,
  sub,
  tone = TONE.neutral,
  trend,
  delay = 0,
  accent = false,
  delta,
  /** When true, a positive delta is rendered as RED (because the
   *  metric — losses, costs — is bad when it goes up). Default false
   *  treats up as good (revenue, profit, count). */
  invertDelta = false,
}: {
  icon: string;
  label: string;
  value: string;
  sub?: string;
  /** icon-tile background + figure colour (light and night) — dashTone.ts */
  tone?: Tone;
  trend?: 'up' | 'down' | 'neutral';
  /** Stagger delay in ms for the entry animation. */
  delay?: number;
  /** Pulls in the brand-gold top stripe — used for the primary KPI on the row. */
  accent?: boolean;
  /** Percent change vs prior period (e.g. 0.12 for +12%). Null when no
   *  comparable prior window exists. */
  delta?: number | null;
  invertDelta?: boolean;
}) {
  const { t } = useT();
  // Resolve the delta chip's color/sign. Same magnitude is muted gray.
  let chipTone: ToneName = 'muted';
  let chipText = '';
  if (delta != null && Number.isFinite(delta)) {
    const pct = Math.round(delta * 100);
    chipText = `${pct >= 0 ? '+' : ''}${pct}%`;
    const isGood = invertDelta ? delta < 0 : delta > 0;
    const isBad  = invertDelta ? delta > 0 : delta < 0;
    if (pct === 0)      chipTone = 'muted';
    else if (isGood)    chipTone = 'done';
    else if (isBad)     chipTone = 'stuck';
    else                chipTone = 'muted';
  }
  return (
    <div
      className="bg-white rounded-xl p-5 hover:-translate-y-0.5 transition-all duration-200 border relative overflow-hidden animate-[fadeInUp_0.5s_ease-out_both]"
      style={{
        borderColor: 'rgba(201, 162, 74, 0.15)',
        boxShadow: '0 1px 3px rgba(31, 41, 55, 0.04), 0 8px 24px rgba(31, 41, 55, 0.04)',
        animationDelay: `${delay}ms`,
      }}
    >
      {accent && (
        <span
          aria-hidden
          className="absolute top-0 left-0 right-0 h-[3px]"
          style={{ background: 'linear-gradient(90deg, #C9A24A, #D4B86A)' }}
        />
      )}
      <div className="flex items-start justify-between mb-3">
        <div className={`w-10 h-10 rounded-xl flex items-center justify-center text-xl ${tone.tile}`}>
          {icon}
        </div>
        {chipText ? (
          <span
            className={`text-xs font-semibold px-2 py-0.5 rounded-full inline-flex items-center gap-0.5 ${chip(chipTone)}`}
            title={t('Variation vs période précédente')}
          >
            {chipText}
          </span>
        ) : trend && (
          <span
            className={`text-xs font-semibold px-2 py-0.5 rounded-full inline-flex items-center gap-0.5 ${
              trend === 'up' ? 'bg-green-50 text-green-700' :
              trend === 'down' ? 'bg-red-50 text-red-700' :
              'bg-gray-50 text-gray-400'
            }`}
          >
            {trend === 'up' ? '↑' : trend === 'down' ? '↓' : '—'}
          </span>
        )}
      </div>
      <p className="text-[11px] font-medium text-gray-400 uppercase tracking-wider mb-1.5">{label}</p>
      <p
        className={`text-[28px] leading-none mb-1 ${tone.ink}`}
        style={{
          fontFamily: "'Fraunces', 'Cormorant Garamond', Georgia, serif",
          fontWeight: 500,
          letterSpacing: '-0.01em',
        }}
      >
        {value}
      </p>
      {sub && <p className="text-[11px] text-gray-400 mt-1.5">{sub}</p>}
    </div>
  );
}

// ─── Delta helper ────────────────────────────────────────────────────────────
// Percentage change vs prior period. Returns null when:
//   • There's no prior data (lifetime/all view)
//   • Prior was 0 AND current > 0 (no meaningful baseline)
// Returns 0 when both periods are 0 so the chip renders as muted gray.
function computeDelta(current: number, previous: number | null | undefined): number | null {
  if (previous == null) return null;
  if (previous === 0) return current === 0 ? 0 : null;
  return (current - previous) / Math.abs(previous);
}

// ─── Action tile ─────────────────────────────────────────────────────────────
// Compact KPI used in the "Actions requises" row. Big number, color
// matches severity, zero-state is muted so a clean dashboard reads
// as "all good" rather than "broken empty card". Clicking deep-links
// into /admin/orders pre-filtered by status.
function ActionTile({ icon, label, value, sub, color, to }: { icon: string; label: string; value: number; sub: string; color: string; to: string }) {
  const muted = value === 0;
  return (
    <Link
      to={to}
      className={`bg-white rounded-xl p-5 border transition-all animate-[fadeInUp_0.5s_ease-out_both] block hover:-translate-y-0.5 ${muted ? 'border-black/[0.06] dark:border-adm-line' : ''}`}
      style={{
        borderColor: muted ? undefined : `${color}40`,
        boxShadow: muted ? 'none' : `0 1px 3px ${color}10, 0 8px 24px ${color}10`,
      }}
    >
      <div className="flex items-center justify-between mb-2">
        <span className="text-xl">{icon}</span>
        <span
          className="text-[28px] leading-none font-bold"
          style={{
            // a series hue (≥ 3:1 on a night card at this size); zero = faint
            color: muted ? 'var(--color-adm-faint)' : color,
            fontFamily: "'Fraunces', 'Cormorant Garamond', Georgia, serif",
            fontWeight: 500,
          }}
        >
          {value}
        </span>
      </div>
      <p className="text-[11px] font-medium text-gray-500 uppercase tracking-wider">{label}</p>
      <p className="text-[11px] text-gray-400 mt-0.5">{sub}</p>
    </Link>
  );
}

// ─── Section heading ─────────────────────────────────────────────────────────
// Small uppercase eyebrow + thin gold rule, used to break the dashboard
// into Performance / Pipeline / Formules / Insights sections without
// loud H2s.
function SectionHeading({ label }: { label: string }) {
  return (
    <div className="flex items-center gap-3 mb-3">
      <span
        className="text-[11px] uppercase tracking-[0.18em] font-semibold text-adm-gold-ink"
      >
        {label}
      </span>
      <div
        className="flex-1 h-px"
        style={{ background: 'linear-gradient(90deg, rgba(201, 162, 74, 0.35), transparent)' }}
      />
    </div>
  );
}

// ─── Traffic-source breakdown ─────────────────────────────────────────────────
// Compact horizontal-bar list: icon + label + count + share. Sorted by count
// desc so the dominant channel reads first. Warm/gold tokens, mobile-first.

function TrafficSourceCard({
  bySource,
}: {
  bySource: Record<string, { orders: number; revenue: number }>;
}) {
  const { t } = useT();
  const rows = Object.entries(bySource ?? {})
    .map(([source, v]) => ({ source, ...v }))
    .filter((r) => r.orders > 0)
    .sort((a, b) => b.orders - a.orders);

  const total = rows.reduce((s, r) => s + r.orders, 0);
  if (total === 0) return null;
  const maxOrders = Math.max(...rows.map((r) => r.orders));

  return (
    <div
      className="bg-white rounded-xl mb-7 border overflow-hidden animate-[fadeInUp_0.5s_ease-out_both]"
      style={{
        borderColor: 'rgba(201, 162, 74, 0.15)',
        boxShadow: '0 1px 3px rgba(31, 41, 55, 0.04), 0 8px 24px rgba(31, 41, 55, 0.04)',
        animationDelay: '480ms',
      }}
    >
      <div className="px-5 py-4 border-b" style={{ borderColor: 'rgba(201, 162, 74, 0.15)' }}>
        <h3
          className="text-lg text-[#1F2937] dark:text-adm-ink"
          style={{
            fontFamily: "'Fraunces', 'Cormorant Garamond', Georgia, serif",
            fontWeight: 500,
          }}
        >
          📊 {t('Sources de trafic')}
        </h3>
      </div>
      <div className="divide-y divide-gray-50">
        {rows.map((r) => {
          const meta = sourceMeta(r.source);
          const pct = Math.round((r.orders / total) * 100);
          const barPct = Math.max(4, Math.round((r.orders / maxOrders) * 100));
          return (
            <div key={r.source} className="px-5 py-3">
              <div className="flex items-center gap-3">
                <span
                  className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-sm"
                  style={{ background: 'rgba(201, 162, 74, 0.10)' }}
                  aria-hidden
                >
                  {meta.emoji}
                </span>
                <span className="flex-1 min-w-0 text-sm font-semibold text-gray-800 truncate">
                  {meta.label}
                </span>
                <span className="shrink-0 text-right">
                  <span className="text-sm font-bold text-gray-800">{r.orders}</span>
                  <span className="ml-1.5 text-xs text-gray-400">{pct}%</span>
                </span>
              </div>
              <div
                className="mt-2 h-1.5 w-full rounded-full overflow-hidden"
                style={{ background: 'rgba(201, 162, 74, 0.08)' }}
              >
                <div
                  className="h-full rounded-full"
                  style={{
                    width: `${barPct}%`,
                    background: 'linear-gradient(90deg, #C9A04C, #E0BE73)',
                  }}
                />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─── Sparkline (mini bar chart) ───────────────────────────────────────────────

function Sparkline({
  data,
  days: daysProp,
}: {
  data: Record<string, { orders: number; revenue: number }>;
  days: number; // 0 = auto (all available, capped at 30)
}) {
  const { t } = useT();
  const entries = useMemo(() => {
    const sorted = Object.entries(data).sort(([a], [b]) => a.localeCompare(b));
    const days = daysProp && daysProp > 0 ? daysProp : Math.min(sorted.length, 30);
    return sorted.slice(-days);
  }, [data, daysProp]);

  if (!entries.length) return null;
  const maxOrders = Math.max(...entries.map(([, v]) => v.orders), 1);
  const totalOrders = entries.reduce((s, [, v]) => s + v.orders, 0);
  const totalRevenue = entries.reduce((s, [, v]) => s + v.revenue, 0);

  return (
    <div
      className="bg-white rounded-xl p-5 border animate-[fadeInUp_0.5s_ease-out_both]"
      style={{
        borderColor: 'rgba(201, 162, 74, 0.15)',
        boxShadow: '0 1px 3px rgba(31, 41, 55, 0.04), 0 8px 24px rgba(31, 41, 55, 0.04)',
        animationDelay: '350ms',
      }}
    >
      <div className="flex items-end justify-between mb-4 flex-wrap gap-2">
        <div>
          <p className="text-[11px] uppercase tracking-wider text-gray-400 font-medium">{t('Activité')}</p>
          <h3
            className="text-lg leading-tight text-[#1F2937] dark:text-adm-ink"
            style={{ fontFamily: "'Fraunces', 'Cormorant Garamond', Georgia, serif", fontWeight: 500 }}
          >
            {t('Commandes payées par jour')}
          </h3>
        </div>
        <div className="text-right">
          <p className="text-xs text-gray-400">{t('Cumulé')}</p>
          <p className="text-sm font-semibold text-adm-gold-ink">
            {totalOrders} {t('cmd')} · {totalRevenue.toLocaleString('fr-MA')} MAD
          </p>
        </div>
      </div>
      {/* The previous layout used items-end + flex-col children without
          h-full on the columns, so each bar's `height: N%` resolved to
          a percentage of the column's intrinsic content height (just the
          bar + label, ~12 px). Bars looked like thin gold lines no matter
          how many orders the day had. Restructured so each column is
          `h-full flex-col` with a `flex-1` spacer on top — now the bar
          pushes down to the baseline and percentage heights are correct
          against the full 112 px chart height. */}
      <div className="flex items-stretch gap-1.5 h-28">
        {entries.map(([day, val]) => (
          <div key={day} className="flex-1 h-full flex flex-col group relative">
            <div className="absolute bottom-full mb-1.5 left-1/2 -translate-x-1/2 hidden group-hover:flex flex-col items-center bg-gray-900 dark:bg-adm-surface-3 dark:ring-1 dark:ring-adm-line-strong text-white text-[10px] rounded px-2 py-1 whitespace-nowrap z-10 shadow-lg">
              <span className="font-semibold">{day.slice(5)}</span>
              <span>{val.orders} {t('cmd')} · {val.revenue.toLocaleString('fr-MA')} MAD</span>
            </div>
            {/* Spacer pushes the bar to the bottom — percentage height
                on the bar now means percentage of the COLUMN height. */}
            <div className="flex-1" />
            <div
              className="w-full rounded-t-md transition-all min-h-[3px] group-hover:opacity-90"
              style={{
                height: `${(val.orders / maxOrders) * 100}%`,
                background: 'linear-gradient(to top, #C9A24A, #D4B86A)',
              }}
            />
            {entries.length <= 14 && (
              <span className="text-[9px] text-gray-300 dark:text-adm-faint hidden sm:block text-center mt-1">{day.slice(8)}</span>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── Main Dashboard ───────────────────────────────────────────────────────────

// ─── Missing-cover "notify clients" modal (Task 4 D) ─────────────────────────
// The admin added a cover for a destination/event clients searched and left.
// This modal: (1) auto-matches (or lets the admin pick) the new cover for the
// searched term, (2) calls fulfill → pins the cover for each searching order +
// marks the searches resolved, (3) renders a per-client WhatsApp button that
// generates the resume link and records the send via the existing follow-up
// pipeline (wa.me + /follow-up/record). No new send channel — same as Relances.
function waIntl(phone: string | null | undefined): string | null {
  const digits = (phone ?? '').replace(/\D/g, '');
  if (!digits) return null;
  return digits.startsWith('0') ? `212${digits.slice(1)}` : digits;
}

function normForMatch(s: string): string {
  return (s ?? '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase().trim().replace(/\s+/g, ' ');
}

type MissingCoverActionRow = {
  kind: 'missing-cover-searched';
  severity: 'info';
  destination: string;
  query_norm: string;
  search_count: number;
  client_count: number;
  sample: Array<{ name: string | null; phone: string | null; shopify_order_id: string | null }>;
};

function MissingCoverNotifyModal({
  action,
  onClose,
}: {
  action: MissingCoverActionRow;
  onClose: () => void;
}) {
  const { t } = useT();
  const [covers, setCovers] = useState<Cover[]>([]);
  const [coverId, setCoverId] = useState<string>('');
  const [loadingCovers, setLoadingCovers] = useState(true);
  const [fulfilling, setFulfilling] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [clients, setClients] = useState<
    Array<{ shopify_order_id: string | null; order_id: string; customer_name: string | null; customer_phone: string | null }> | null
  >(null);
  // shopify_order_id → continue_url (lazily minted on first WhatsApp click)
  const [links, setLinks] = useState<Record<string, string>>({});
  const [sent, setSent] = useState<Record<string, boolean>>({});
  const [busyId, setBusyId] = useState<string | null>(null);

  // Load all covers (incl. hidden) and auto-match by name/categories/tags to the
  // searched term so the admin usually just confirms.
  useEffect(() => {
    let alive = true;
    coversApi
      .list({ include_hidden: true })
      .then((res) => {
        if (!alive) return;
        const list = res.covers ?? [];
        setCovers(list);
        const target = action.query_norm || normForMatch(action.destination);
        const match = list.find((c) => {
          const hay = normForMatch(
            [c.name, ...(c.categories ?? []), ...(c.search_tags ?? []), c.description ?? '']
              .filter(Boolean)
              .join(' '),
          );
          return hay.includes(target) || target.includes(normForMatch(c.name));
        });
        if (match) setCoverId(match.id);
      })
      .catch(() => setError(t('Impossible de charger les couvertures.')))
      .finally(() => { if (alive) setLoadingCovers(false); });
    return () => { alive = false; };
  }, [action.query_norm, action.destination]);

  const handleFulfill = async () => {
    if (!coverId) return;
    setFulfilling(true);
    setError(null);
    try {
      const res = await coverSearchesApi.fulfill({ cover_id: coverId, query_norm: action.query_norm });
      setClients(res.clients);
    } catch (e) {
      setError(e instanceof Error ? e.message : t('Échec de la mise à jour.'));
    } finally {
      setFulfilling(false);
    }
  };

  const handleNotify = async (client: { shopify_order_id: string | null; customer_name: string | null; customer_phone: string | null }) => {
    const sid = client.shopify_order_id;
    if (!sid) return;
    setBusyId(sid);
    try {
      // 1) Mint (or reuse) the resume link into the cover-selection step.
      let link = links[sid];
      if (!link) {
        const res = await resumeLinkApi.generate(sid);
        link = res.continue_url;
        setLinks((prev) => ({ ...prev, [sid]: link }));
      }
      // 2) Build the "cover now available" message and open WhatsApp.
      const firstName = (client.customer_name ?? '').split(' ')[0] ?? '';
      // `cover_now_available` was deleted with the funnel's cover step
      // (2026-08-12). The client now picks from the catalogue link, so this
      // reuses the cover-choice message and points at the same place.
      const msg = renderMessage(
        'cover_not_chosen',
        { firstName, destination: action.destination, link },
        null,
      );
      const intl = waIntl(client.customer_phone);
      if (intl) {
        window.open(`https://wa.me/${intl}?text=${encodeURIComponent(msg)}`, '_blank', 'noopener,noreferrer');
      }
      // 3) Record the send via the existing follow-up pipeline (fire-and-forget).
      ordersApi.recordFollowUp(sid, 'cover_now_available').catch(() => { /* non-blocking */ });
      setSent((prev) => ({ ...prev, [sid]: true }));
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(31,41,55,0.45)' }} onClick={onClose}>
      <div
        className="bg-white rounded-2xl w-full max-w-md max-h-[85vh] overflow-y-auto shadow-2xl"
        style={{ border: '1px solid rgba(201,162,74,0.25)' }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-5 py-4 border-b" style={{ borderColor: 'rgba(201,162,74,0.15)' }}>
          <div className="flex items-center justify-between">
            <h3 className="text-base font-bold text-gray-800">
              🔍 « {action.destination} »
            </h3>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl leading-none" aria-label={t('Fermer')}>✕</button>
          </div>
          <p className="text-xs text-gray-500 mt-1">
            {t('Recherchée par {c} clients · {n} recherches', { c: action.client_count, n: action.search_count })}
          </p>
        </div>

        <div className="p-5">
          {!clients ? (
            <>
              <p className="text-sm text-gray-600 mb-3">
                {t('Sélectionnez la couverture que vous venez d’ajouter pour « {dest} ». Elle sera épinglée en tête du choix de couverture pour ces clients.', { dest: action.destination })}
              </p>
              <label className="block text-[11px] font-semibold uppercase tracking-wider text-gray-500 mb-1">{t('Couverture')}</label>
              <select
                value={coverId}
                onChange={(e) => setCoverId(e.target.value)}
                disabled={loadingCovers}
                className="w-full min-h-[44px] px-3 rounded-lg border text-sm bg-white"
                style={{ borderColor: 'rgba(201,162,74,0.3)' }}
              >
                <option value="">{loadingCovers ? t('Chargement…') : t('— Choisir une couverture —')}</option>
                {covers.map((c) => (
                  <option key={c.id} value={c.id}>{c.name}{c.is_visible ? '' : ` ${t('(masquée)')}`}</option>
                ))}
              </select>
              {error && <p className="text-xs text-red-500 mt-2">{error}</p>}
              <button
                onClick={handleFulfill}
                disabled={!coverId || fulfilling}
                className="mt-4 w-full min-h-[44px] rounded-xl text-sm font-semibold text-white transition-all disabled:opacity-50"
                style={{ background: '#C9A24A' }}
              >
                {fulfilling ? t('Mise à jour…') : t('Couverture ajoutée — prévenir les clients')}
              </button>
            </>
          ) : (
            <>
              <p className="text-sm text-gray-700 mb-3">
                {t('✅ Couverture épinglée pour {n} commandes. Prévenez chaque client :', { n: clients.length })}
              </p>
              <div className="space-y-2">
                {clients.length === 0 && (
                  <p className="text-sm text-gray-500">{t('Aucun client avec une commande rattachée à prévenir.')}</p>
                )}
                {clients.map((client) => {
                  const sid = client.shopify_order_id;
                  const intl = waIntl(client.customer_phone);
                  const isSent = sid ? sent[sid] : false;
                  return (
                    <div key={client.order_id} className="flex items-center gap-2 px-3 py-2 rounded-lg bg-[#FAF7EF] dark:bg-adm-surface-2">
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold text-gray-800 truncate">{client.customer_name ?? t('Client')}</p>
                        <p className="text-xs text-gray-500 truncate">{client.customer_phone ? formatPhone(client.customer_phone) : t('Pas de téléphone')}</p>
                      </div>
                      <button
                        onClick={() => handleNotify(client)}
                        disabled={!sid || !intl || busyId === sid}
                        className={`px-3 min-h-[36px] rounded-md text-xs font-medium text-white shrink-0 disabled:opacity-40 ${isSent ? 'bg-[#6B7280] dark:bg-adm-surface-3' : 'bg-[#128C7E]'}`}
                      >
                        {busyId === sid ? '…' : isSent ? t('Renvoyer') : 'WhatsApp'}
                      </button>
                    </div>
                  );
                })}
              </div>
              <button onClick={onClose} className="mt-4 w-full min-h-[44px] rounded-xl text-sm font-semibold text-gray-600 border border-black/10 dark:border-adm-line-strong">
                {t('Terminé')}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default function AdminReportsPage() {
  const { t } = useT();
  const { can } = usePermissions();  // RBAC: hide finance widgets w/o finance.view
  const isAdminRole = useAuthStore((st) => st.profile?.role === 'admin');
  const isFullAdmin = isAdminRole || can('*');
  const canFinance = can('finance.view');
  const canPush = isFullAdmin || can('whatsapp.manage');
  const isAgent = !isFullAdmin && can('orders.edit_status');
  const [dates, setDates] = useState<DateRange>(() => presetRange('7d'));
  // The missing-cover row the admin clicked « prévenir » on. Null = closed.
  const [notifyAction, setNotifyAction] = useState<MissingCoverActionRow | null>(null);
  const location = useLocation();

  const rangeKey = `${dates.from ?? 'all'}_${dates.to ?? 'now'}`;
  const { data, isLoading } = useQuery({
    queryKey: ['dashboard-stats', rangeKey],
    queryFn: () => ordersApi.getStats(dates),
    staleTime: 60_000,
  });
  const { data: ordersData } = useQuery({
    queryKey: ['admin-orders-recent', isAgent ? 'mine' : 'all'],
    queryFn: () => ordersApi.list({ limit: '20', page: '1', ...(isAgent ? { mine: '1' } : {}) }),
    staleTime: 30_000,
  });
  // « Dossiers anciens » — the orders the home folds away (no activity for 14 days).
  const { data: staleData, isLoading: staleLoading } = useQuery({
    queryKey: ['home-stale'],
    queryFn: () => dashboardApi.stale(),
    enabled: canFinance,
    staleTime: 60_000,
  });

  // The home links here with #dossiers-anciens: scroll once the section exists.
  useEffect(() => {
    if (!location.hash) return;
    const id = location.hash.slice(1);
    const tm = window.setTimeout(() => document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 250);
    return () => window.clearTimeout(tm);
  }, [location.hash, staleData]);

  const s = data?.stats;
  const recentOrders = ordersData?.orders ?? [];
  const followActions = (s?.actions ?? []).filter((a) => a.kind === 'stalled-pending' || a.kind === 'stuck-in-transit');
  const coverIdeas = (s?.actions ?? []).filter((a): a is MissingCoverActionRow => a.kind === 'missing-cover-searched');
  const finished = s ? s.deliveredCount + s.lostCount : 0;
  const delivRate = rateOrTooEarly(s?.deliveredCount ?? 0, finished);

  return (
    <AdminLayout
      title={t('Rapports')}
      subtitle={t('Le détail qui a quitté l’accueil — même période pour tout')}
      actions={<DateRangePicker value={dates} onChange={setDates} align="right" />}
    >
      {/* ── CALCULATEUR DE PROFIT (owner, 2026-10-22) — every cost line a
          switch; all on = the official Profit net. Its own period buttons. */}
      {canFinance && <ProfitCalculator />}

      {/* Coupons (2026-10-28): one line, only once coupons exist. */}
      <CouponsReportLine enabled={isFullAdmin || can('coupons.manage')} />

      {/* ── DOSSIERS ANCIENS — open orders with no activity for 14 days. The
          home's « À faire » and « Pipeline » fold them into one line that
          links here (owner, 2026-10-21, option A). */}
      {canFinance && (
        <section id="dossiers-anciens" className="scroll-mt-24">
          <SectionHeading label={t('Dossiers anciens')} />
          <StaleOrdersCard data={staleData} isLoading={staleLoading} />
        </section>
      )}

      {isLoading ? (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 mb-6">
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="bg-white rounded-xl border border-gray-100 p-5 h-28 animate-pulse" />
          ))}
        </div>
      ) : s ? (
        <>
          {/* ── Money in flight — moved from the home (« En route », « Perdu »).
              The profit itself is on the home, from the ONE shared formula. */}
          {canFinance && (<>
            <SectionHeading label={`${t('Colis en route et perdus')}${s.previous ? ` · ${t('vs période précédente')}` : ''}`} />
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-7">
              <KpiCard
                icon="🚚"
                label={t('En route')}
                value={`${s.cashInTransit.toLocaleString('fr-MA')} MAD`}
                sub={t('{n} colis en transit', { n: s.inTransitCount })}
                tone={TONE.info}
                delta={computeDelta(s.cashInTransit, s.previous?.cashInTransit ?? null)}
              />
              <KpiCard
                icon="⚠️"
                label={t('Perdu (retours + refus)')}
                value={`${s.cashLost.toLocaleString('fr-MA')} MAD`}
                sub={`${t('{n} colis', { n: s.lostCount })} · ${delivRate.kind === 'tooEarly' ? t('trop tôt pour un taux') : s.lossRate == null ? '—' : Math.round(s.lossRate * 100) + '% ' + t('taux')}`}
                tone={TONE.stuck}
                delta={computeDelta(s.cashLost, s.previous?.cashLost ?? null)}
                invertDelta
                delay={60}
              />
            </div>
          </>)}

          {/* ── COÛTS IA (owner, 2026-10-22) — the AI ledger: per day, feature,
              model, delivered order; the OpenRouter check. The home's AI tiles
              link here (#couts-ia). */}
          {canFinance && (
            <section id="couts-ia" className="scroll-mt-24">
              <SectionHeading label={t('Coûts IA')} />
              {isAdminRole && (
                <p className="-mt-1 mb-2 text-xs text-right">
                  <Link to="/admin/ai-compare" className="font-semibold text-adm-gold-ink hover:underline">{t('Comparer les modèles')} →</Link>
                </p>
              )}
              <AiCostsCard range={dates} />
              <AiCallsList range={dates} />
            </section>
          )}

          {/* RENTABILITÉ — ad cost per confirmed album + the shared profit. */}
          {can('marketing.manage') && (
            <>
              <SectionHeading label={t('Rentabilité')} />
              <ProfitabilityCard range={dates} />
            </>
          )}

          {isFullAdmin && (<>
            <SectionHeading label={t('Livraisons AMEEX')} />
            <AmeexInsightsCard range={dates} />
          </>)}

          {/* ── OPERATIONAL HEALTH — a rate on fewer than 10 parcels is noise:
              « 4 colis — trop tôt » instead of a percentage. */}
          {isFullAdmin && (<>
            <SectionHeading label={t('Performance opérationnelle')} />
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-7">
              <KpiCard
                icon="✅"
                label={t('Taux de livraison')}
                value={delivRate.kind === 'tooEarly'
                  ? (finished === 0 ? '—' : t('{n} colis — trop tôt', { n: finished }))
                  : `${delivRate.pct}%`}
                sub={delivRate.kind === 'tooEarly'
                  ? (finished === 0 ? t('Aucun colis terminé') : t('Pas de taux sous {n} colis terminés', { n: MIN_SAMPLE }))
                  : t('{d} livrés sur {total}', { d: s.deliveredCount, total: finished })}
                tone={delivRate.kind === 'tooEarly' ? TONE.muted : delivRate.pct >= 80 ? TONE.done : delivRate.pct >= 60 ? TONE.caution : TONE.stuck}
              />
              <KpiCard
                icon="⏱️"
                label={t('Délai moyen de livraison')}
                value={s.avgDeliveryDays == null ? '—' : s.deliveredCount < MIN_SAMPLE ? t('trop tôt') : `${s.avgDeliveryDays.toFixed(1)} j`}
                sub={s.deliveredCount < MIN_SAMPLE && s.avgDeliveryDays != null
                  ? t('{n} colis livrés seulement', { n: s.deliveredCount })
                  : t('De la commande à la livraison')}
                tone={TONE.slate}
                delay={60}
              />
              <KpiCard
                icon="🔁"
                label={t('Clients fidèles')}
                value={String(s.repeatCustomers)}
                sub={s.repeatCustomers === 0 ? t('Aucune commande répétée') : t('Ont commandé +1 fois')}
                tone={TONE.violet}
                delay={120}
              />
            </div>
          </>)}

          {isFullAdmin && (
            <>
              <SectionHeading label={t('Commandes confirmées')} />
              <ConfirmedOrdersCard range={dates} />
            </>
          )}

          {/* ── À SUIVRE — « Clients à relancer » + « Colis bloqués », moved
              from the old « Actions requises » row. */}
          {(s.actionsCount.stalled + s.actionsCount.stuckInTransit) > 0 && (
            <>
              <SectionHeading label={t('À suivre')} />
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <ActionTile color="#F59E0B" icon="⏰" label={t('Clients à relancer')} value={s.actionsCount.stalled} sub={t('+ de 7 jours sans soumission')} to="/admin/orders?status=pending-album" />
                <ActionTile color="#EF4444" icon="🚨" label={t('Colis bloqués')} value={s.actionsCount.stuckInTransit} sub={t('Pas de mouvement +5j')} to="/admin/orders?status=shipped" />
              </div>
              {followActions.length > 0 && <FollowList actions={followActions} />}
            </>
          )}

          {/* ── IDÉES DE COUVERTURES DEMANDÉES — clients searched a destination
              we have no cover for (was « Couvertures manquantes »). */}
          {coverIdeas.length > 0 && (
            <>
              <SectionHeading label={t('Idées de couvertures demandées')} />
              <div className="bg-white rounded-xl border mb-7 overflow-hidden"
                   style={{ borderColor: 'rgba(201, 162, 74, 0.15)', boxShadow: '0 1px 3px rgba(31, 41, 55, 0.04)' }}>
                <div className="divide-y divide-gray-50 max-h-80 overflow-y-auto">
                  {coverIdeas.slice(0, 20).map((a, i) => (
                    <div key={`missing-${a.query_norm}-${i}`} className="px-5 py-3 flex items-center gap-3 hover:bg-gray-50 transition-colors">
                      <span className="w-2 h-2 rounded-full shrink-0" style={{ background: '#0EA5E9' }} />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold text-gray-800 truncate">🔍 « {a.destination} »</p>
                        <p className="text-xs text-gray-500 truncate">
                          {t('Recherchée par {c} clients · {n} recherches', { c: a.client_count, n: a.search_count })}
                        </p>
                      </div>
                      <button
                        onClick={() => setNotifyAction(a)}
                        className="px-2.5 py-1.5 rounded-md text-xs font-medium text-white shrink-0"
                        style={{ background: '#0EA5E9' }}
                      >
                        {t('Couverture ajoutée — prévenir')}
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            </>
          )}

          {isFullAdmin && <CityHealthSection />}

          {isFullAdmin && (<>
            <SectionHeading label={t('Insights')} />
            {Object.keys(s.byDay).length > 0 ? (
              <div className="mb-7">
                <Sparkline data={s.byDay} days={spanDays(dates)} />
              </div>
            ) : (
              <div
                className="mb-7 bg-white rounded-xl p-7 border text-center"
                style={{ borderColor: 'rgba(201, 162, 74, 0.15)', boxShadow: '0 1px 3px rgba(31, 41, 55, 0.04)' }}
              >
                <p className="text-sm text-gray-500">{t('Pas encore de commandes sur cette période.')}</p>
              </div>
            )}
            {s.topClients.length > 0 && <TopClientsCard clients={s.topClients} />}
            <TrafficSourceCard bySource={s.bySource} />
          </>)}
        </>
      ) : null}

      {/* The agent never speaks over the team — target 0 a day (2026-10-16). */}
      {canPush && (<>
        <SectionHeading label={t('Équipe et assistant')} />
        <AgentOverlapCard />
      </>)}

      {isFullAdmin && <LatestSubmittedCard />}
      <RecentOrdersCard orders={recentOrders} />

      {notifyAction && (
        <MissingCoverNotifyModal action={notifyAction} onClose={() => setNotifyAction(null)} />
      )}
    </AdminLayout>
  );
}

// ─── Dossiers anciens ─────────────────────────────────────────────────────────
const STALE_STEP_LABEL: Record<string, string> = {
  photos: 'Photos reçues', album: 'Album fait', validated: 'Validé', printing: 'Impression', shipped: 'Expédié',
};

function StaleOrdersCard({ data, isLoading }: { data?: StaleResponse; isLoading: boolean }) {
  const { t } = useT();
  const [all, setAll] = useState(false);
  if (isLoading) return <div className="mb-7 bg-white rounded-xl border border-gray-100 h-24 animate-pulse" />;
  if (!data) return null;
  const rows = all ? data.stale : data.stale.slice(0, 12);
  return (
    <div className="bg-white rounded-xl mb-7 border overflow-hidden"
         style={{ borderColor: 'rgba(201, 162, 74, 0.15)', boxShadow: '0 1px 3px rgba(31, 41, 55, 0.04), 0 8px 24px rgba(31, 41, 55, 0.04)' }}>
      <div className="px-5 py-3 border-b flex items-center gap-3 flex-wrap" style={{ borderColor: 'rgba(201, 162, 74, 0.15)' }}>
        <p className="text-sm text-adm-ink-2 flex-1 min-w-0">
          {data.total === 0
            ? t('Aucun dossier sans activité depuis 14 jours.')
            : t('{n} commandes sans activité depuis plus de 14 jours', { n: data.total })}
        </p>
        {data.total > 0 && (
          <span className="text-sm font-semibold text-adm-ink" style={{ fontFamily: "'Fraunces', Georgia, serif" }}>
            {formatDH(data.value)}
          </span>
        )}
      </div>
      {rows.length > 0 && (
        <div className="divide-y divide-gray-50">
          {rows.map((o) => (
            <Link key={o.shopify_order_id} to={`/admin/orders/${encodeURIComponent(o.shopify_order_id)}`}
              className="px-5 py-2.5 grid grid-cols-[minmax(0,1fr)_auto] md:grid-cols-[minmax(0,1fr)_140px_90px_90px] gap-x-3 gap-y-0.5 items-center hover:bg-gray-50">
              <span className="min-w-0">
                <b className="block text-sm font-semibold text-adm-ink truncate">{o.customer_name || t('Cliente')}</b>
                <small className="block text-xs text-adm-faint truncate">{o.order_ref}</small>
              </span>
              <span className="hidden md:block text-xs text-adm-muted truncate">{t(STALE_STEP_LABEL[o.step] ?? o.status)}</span>
              <span className="text-xs font-semibold text-status-waiting text-right whitespace-nowrap">{t('{n} j', { n: o.days })}</span>
              <span className="hidden md:block text-sm text-adm-ink-2 text-right whitespace-nowrap">{formatDH(o.value)}</span>
            </Link>
          ))}
        </div>
      )}
      {data.stale.length > rows.length && (
        <button type="button" onClick={() => setAll(true)} className="w-full px-5 py-2.5 text-left text-xs font-semibold text-adm-gold-ink border-t" style={{ borderColor: 'rgba(201, 162, 74, 0.15)' }}>
          {t('Voir tout ({n})', { n: data.stale.length })} ↓
        </button>
      )}
    </div>
  );
}

// ─── Follow-up list (Clients à relancer / Colis bloqués) ─────────────────────
type FollowAction = Extract<NonNullable<ReturnType<typeof ordersApi.getStats> extends Promise<infer R> ? R : never>['stats']['actions'][number], { kind: 'stalled-pending' | 'stuck-in-transit' }>;

function FollowList({ actions }: { actions: FollowAction[] }) {
  const { t } = useT();
  return (
    <div className="bg-white rounded-xl border mb-7 overflow-hidden"
         style={{ borderColor: 'rgba(201, 162, 74, 0.15)', boxShadow: '0 1px 3px rgba(31, 41, 55, 0.04)' }}>
      <div className="divide-y divide-gray-50 max-h-80 overflow-y-auto">
        {actions.slice(0, 20).map((a, i) => {
          const stalled = a.kind === 'stalled-pending';
          const title = stalled
            ? `${a.customer_name ?? t('Client inconnu')} — ${t('{n}j sans soumission', { n: a.days_since_created })}`
            : `${a.customer_name ?? t('Client inconnu')} — ${t('colis bloqué {n}j', { n: a.days_since_event })}`;
          const detail = stalled
            ? (a.customer_phone ? `📱 ${formatPhone(a.customer_phone)}` : t('Pas de téléphone'))
            : `AMEEX ${a.ameex_parcel_code ?? '—'} · ${t('statut')} ${a.ameex_status ?? '—'}`;
          const phone = stalled ? a.customer_phone : null;
          const waMessage = stalled
            ? t('Bonjour {name} 👋, c’est Storiz. On a remarqué que votre album n’est pas encore finalisé depuis {n} jours. Avez-vous besoin d’aide pour le compléter ? On reste à votre disposition !', { name: a.customer_name?.split(' ')[0] ?? '', n: a.days_since_created })
            : null;
          const cleanPhone = phone ? phone.replace(/\D/g, '').replace(/^0/, '212').replace(/^212212/, '212') : null;
          return (
            <div key={`${a.kind}-${a.shopify_order_id}-${i}`} className="px-5 py-3 flex items-center gap-3 hover:bg-gray-50 transition-colors">
              <span className="w-2 h-2 rounded-full shrink-0" style={{ background: stalled ? '#F59E0B' : '#EF4444' }} />
              <Link to={`/admin/orders/${a.shopify_order_id}`} className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-gray-800 truncate">{title}</p>
                <p className="text-xs text-gray-500 truncate">{detail}</p>
              </Link>
              {cleanPhone && (
                <a
                  href={`https://wa.me/${cleanPhone}${waMessage ? `?text=${encodeURIComponent(waMessage)}` : ''}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  title={t('Envoyer un message de relance')}
                  className="px-2 py-1 rounded-md text-xs font-medium text-white shrink-0"
                  style={{ background: '#128C7E' }}
                >
                  WhatsApp
                </a>
              )}
              <Link to={`/admin/orders/${a.shopify_order_id}`} className="text-xs text-gray-400 shrink-0">→</Link>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─── Top clients ──────────────────────────────────────────────────────────────
function TopClientsCard({ clients }: { clients: Array<{ name: string; phone: string; orders: number; revenue: number; delivered_count: number; delivered_revenue: number }> }) {
  const { t } = useT();
  return (
    <div
      className="bg-white rounded-xl mb-7 border overflow-hidden"
      style={{ borderColor: 'rgba(201, 162, 74, 0.15)', boxShadow: '0 1px 3px rgba(31, 41, 55, 0.04), 0 8px 24px rgba(31, 41, 55, 0.04)' }}
    >
      <div className="px-5 py-4 border-b" style={{ borderColor: 'rgba(201, 162, 74, 0.15)' }}>
        <h3 className="text-lg text-[#1F2937] dark:text-adm-ink" style={{ fontFamily: "'Fraunces', 'Cormorant Garamond', Georgia, serif", fontWeight: 500 }}>
          🏆 {t('Meilleurs clients')}
        </h3>
      </div>
      <div className="divide-y divide-gray-50">
        {clients.map((client, i) => (
          <div key={i} className="px-5 py-3 flex items-center gap-3">
            <span className="w-6 h-6 rounded-full bg-gray-100 flex items-center justify-center text-xs font-bold text-gray-500 shrink-0">{i + 1}</span>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-gray-800 truncate">{client.name}</p>
              {client.phone && (
                <a href={`https://wa.me/${client.phone.replace(/[^0-9]/g, '')}`} target="_blank" rel="noopener noreferrer"
                  className="text-xs text-[#128C7E] dark:text-[#25D366] hover:underline">
                  {formatPhone(client.phone)}
                </a>
              )}
            </div>
            <div className="text-right shrink-0">
              <p className="text-sm font-bold text-gray-800">{client.delivered_revenue.toLocaleString('fr-MA')} MAD</p>
              <p className="text-xs text-gray-400">
                {t('{n} livrés', { n: client.delivered_count })}
                {client.orders > client.delivered_count && (
                  <span className="text-gray-300"> · {t('{n} en cours', { n: client.orders - client.delivered_count })}</span>
                )}
              </p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── Latest submitted album — realtime card ──────────────────────────────────
// Subscribes to `orders` table changes and pulls the most-recently-submitted
// album. Updates the moment a customer hits submit on the editor.
function LatestSubmittedCard() {
  const { t } = useT();
  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ['latest-submitted-album'],
    queryFn: async () => {
      const { data: rows } = await supabase
        .from('orders')
        .select('*')
        .eq('status', 'album-submitted')
        .order('album_submitted_at', { ascending: false, nullsFirst: false })
        .limit(1);
      return (rows?.[0] ?? null) as Order | null;
    },
    staleTime: 30_000,
  });

  // Live update: refetch whenever any order's status flips to album-submitted.
  useEffect(() => {
    const channel = supabase
      .channel('dashboard-latest-submitted')
      .on(
        'postgres_changes',
        { event: 'UPDATE', schema: 'public', table: 'orders' },
        (payload) => {
          const newRow = payload.new as { status?: string };
          if (newRow.status === 'album-submitted') {
            queryClient.invalidateQueries({ queryKey: ['latest-submitted-album'] });
          }
        },
      )
      .on(
        'postgres_changes',
        { event: 'INSERT', schema: 'public', table: 'orders' },
        () => {
          // New orders may already be in album-submitted (rare) — refetch.
          queryClient.invalidateQueries({ queryKey: ['latest-submitted-album'] });
        },
      )
      .subscribe();

    return () => { supabase.removeChannel(channel); };
  }, [queryClient]);

  if (isLoading) {
    return (
      <div className="bg-white rounded-xl border border-gray-100 p-5 mb-6 h-24 animate-pulse" />
    );
  }
  if (!data) {
    return (
      <div className="bg-white rounded-xl border border-gray-100 p-5 mb-6">
        <div className="flex items-center gap-3">
          <div className="text-2xl">📥</div>
          <div>
            <h3 className="text-sm font-semibold text-gray-700">{t('Dernier album soumis')}</h3>
            <p className="text-xs text-gray-400 mt-0.5">{t('Aucun album soumis pour le moment.')}</p>
          </div>
        </div>
      </div>
    );
  }

  const order = data;
  const submittedAt = order.album_submitted_at ? new Date(order.album_submitted_at) : null;
  const minutesAgo = submittedAt ? Math.floor((Date.now() - submittedAt.getTime()) / 60_000) : null;
  const timeAgo = minutesAgo == null ? '' :
    minutesAgo < 1 ? t('à l’instant') :
    minutesAgo < 60 ? t('il y a {n} min', { n: minutesAgo }) :
    minutesAgo < 24 * 60 ? t('il y a {n} h', { n: Math.floor(minutesAgo / 60) }) :
    t('il y a {n} j', { n: Math.floor(minutesAgo / 1440) });

  const destinations = [
    order.destination_1 && (order.year_1 ? `${order.destination_1} (${order.year_1})` : order.destination_1),
    order.destination_2 && (order.year_2 ? `${order.destination_2} (${order.year_2})` : order.destination_2),
    order.destination_3 && (order.year_3 ? `${order.destination_3} (${order.year_3})` : order.destination_3),
  ].filter(Boolean).join(' • ');

  return (
    <div className="bg-gradient-to-r from-brand-50 to-white dark:from-adm-gold-wash dark:to-adm-surface rounded-xl border border-brand-200 p-5 mb-6 relative overflow-hidden">
      <div className="absolute top-3 right-4 flex items-center gap-1.5">
        <span className="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse" />
        <span className="text-xs text-green-700 font-medium">{t('en direct')}</span>
      </div>
      <div className="flex items-start gap-4">
        <div className="text-3xl">🎉</div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <h3 className="text-sm font-semibold text-gray-700">{t('Dernier album soumis')}</h3>
            <span className="text-xs text-gray-400">{timeAgo}</span>
          </div>
          <Link
            to={`/admin/orders/${order.shopify_order_id}`}
            className="text-base font-semibold text-brand-500 hover:underline"
          >
            {order.customer_name ?? t('Client')}
          </Link>
          <span className="text-sm text-gray-500"> · {order.shopify_order_name}</span>
          <div className="text-sm text-gray-600 mt-1 truncate">
            {order.bundle && <span className="font-medium">{order.bundle}</span>}
            {destinations && <span> · {destinations}</span>}
            {order.page_count && <span className="text-gray-400"> · {t('{n} papier', { n: order.page_count })}</span>}
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Per-city delivery health ────────────────────────────────────────────────
// Pulls every AMEEX parcel and groups by city. Renders the top 8 cities
// with parcel volume + delivery rate so the owner can see geographic
// patterns (e.g. "Casablanca 95%, Berkane 50% — investigate Berkane").
// Hides itself when AMEEX has no data or the call fails.
function CityHealthSection() {
  const { t } = useT();
  const { data, isLoading, error } = useQuery({
    queryKey: ['ameex-insights-by-city'],
    queryFn: () => ameexApi.insightsByCity(),
    staleTime: 5 * 60_000,  // 5 min — pulling AMEEX hurts, don't refetch on every tab
    refetchOnWindowFocus: false,
  });

  if (isLoading) return null;
  if (error || !data || data.cities.length === 0) return null;

  const top = data.cities.slice(0, 8);
  const maxTotal = Math.max(1, ...top.map(c => c.total));

  // green ≥ 80 %, yellow (orange at night) 60–80 %, red below — classes, so
  // the chip follows night mode (dashTone.ts).
  const rateChip = (rate: number | null) => {
    if (rate == null) return `${TONE.muted.tile} text-adm-faint`;
    if (rate >= 0.8) return chip('done');
    if (rate >= 0.6) return `${TONE.caution.tile} text-[#D97706] dark:text-status-waiting`;
    return chip('stuck');
  };

  return (
    <>
      <SectionHeading label={t('Performance par ville')} />
      <div
        className="bg-white rounded-xl border mb-7 overflow-hidden animate-[fadeInUp_0.5s_ease-out_both]"
        style={{
          borderColor: 'rgba(201, 162, 74, 0.15)',
          boxShadow: '0 1px 3px rgba(31, 41, 55, 0.04), 0 8px 24px rgba(31, 41, 55, 0.04)',
        }}
      >
        <div className="px-5 py-4 border-b flex items-center justify-between" style={{ borderColor: 'rgba(201, 162, 74, 0.15)' }}>
          <p className="text-xs text-gray-500">
            {t('Top {n} villes par volume · données AMEEX directes', { n: top.length })}
          </p>
          <p className="text-xs text-gray-400">
            🟢 ≥ 80% · 🟡 60-80% · 🔴 &lt; 60%
          </p>
        </div>
        <div className="divide-y divide-gray-50">
          {top.map((c) => {
            const widthPct = Math.max(8, Math.round((c.total / maxTotal) * 100));
            const ratePct = c.delivery_rate == null ? '—' : `${Math.round(c.delivery_rate * 100)}%`;
            return (
              <div key={c.city} className="px-5 py-3 grid grid-cols-12 gap-3 items-center">
                <span className="col-span-3 text-sm font-semibold text-gray-800 truncate" title={c.city}>{c.city}</span>
                <div className="col-span-5">
                  <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                    <div className="h-full rounded-full" style={{ width: `${widthPct}%`, background: '#C9A24A' }} />
                  </div>
                  <span className="text-[10px] text-gray-400 mt-1 block">
                    {t('{total} colis · {d} livrés', { total: c.total, d: c.delivered })}
                    {c.in_transit > 0 && ` · ${t('{n} en route', { n: c.in_transit })}`}
                    {(c.returned + c.refused) > 0 && ` · ${t('{n} perdus', { n: c.returned + c.refused })}`}
                  </span>
                </div>
                <span
                  className={`col-span-2 text-center text-xs font-bold py-1 rounded-full ${rateChip(c.delivery_rate)}`}
                  title={t('Taux de livraison (livrés / colis terminés)')}
                >
                  {ratePct}
                </span>
                <span className="col-span-2 text-right text-sm font-semibold text-gray-700">
                  {c.revenue.toLocaleString('fr-MA')} MAD
                </span>
              </div>
            );
          })}
        </div>
      </div>
    </>
  );
}
